import { useEffect, useState } from 'react';

import { LngLatBounds, Map } from 'mapbox-gl';

import { useMap } from '../../contexts/MapContext';
import { ICoordinates } from '../../interfaces/geo';
import { BoundingBoxInputObject } from '../../interfaces/graphql';

interface IMapLocation {
  coordinates: ICoordinates;
  zoom: number;
  bounds: LngLatBounds;
}

export function getBoundingBoxFromLocation(
  location: IMapLocation | null,
): BoundingBoxInputObject | null {
  if (location === null) {
    return null;
  }

  return {
    southWest: {
      latitude: location.bounds.getSouthWest().lat,
      longitude: location.bounds.getSouthWest().lng,
    },
    northEast: {
      latitude: location.bounds.getNorthEast().lat,
      longitude: location.bounds.getNorthEast().lng,
    },
  };
}

function getMapLocation(map?: Map): IMapLocation | null {
  if (!map) {
    return null;
  }

  const { lat, lng } = map.getCenter();
  return {
    coordinates: { latitude: lat, longitude: lng },
    zoom: map.getZoom(),
    bounds: map.getBounds(),
  };
}

export function useMapLocation(): IMapLocation | null {
  const [mapLocation, setMapLocation] = useState<IMapLocation | null>(null);
  const { map } = useMap();

  useEffect(() => {
    if (map) {
      setMapLocation(getMapLocation(map));
    }
  }, [map]);

  useEffect(() => {
    const onMoveEnd = (): void => {
      setMapLocation(getMapLocation(map));
    };

    if (map) {
      map.on('moveend', onMoveEnd);
    }

    return () => {
      if (map) {
        map.off('moveend', onMoveEnd);
      }
    };
  }, [map]);

  return mapLocation;
}
